'use client';

import React, { useMemo, useState } from 'react';
import SidebarMenuItem from '../ui/SidebarMenuItem';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { IconName } from '../ui/Icon';
import Icon from '../ui/Icon';
import { cn, formatNumber, getErrorMessage } from '@/app/lib/utils';
import Dropdown from '../ui/Dropdown';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useUser } from '@/app/context/user';
import { CustomScroll } from '../ui/CustomScroll';
import { useSidebar } from '@/app/context/sidebar';
import ClientLogo from './ClientLogo';
import { getClient, getClients, updateClient } from '@/api/client';
import { Button } from '../ui/Button';
import AddClientModal from '../clients/AddClientModal';
import { useSignalCount } from '@/app/context/signal-count';
import SidebarPlanCard from './SidebarPlanCard';
import { useIntercom } from './IntercomProvider';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Input from '../ui/Input';
import { Client } from '@/types/client';
import useDebounce from '@/app/hooks/use-debounce';
import Spinner from '../ui/Spinner';
import Pill from '../ui/Pill';
import Switch from '../ui/Switch';
import { useToast } from '@/app/hooks/use-toast';
import {
  ArrowLeft,
  BookOpenCheck,
  ChevronDown,
  Globe,
  House,
  Linkedin,
  ListTodo,
  Megaphone,
  Radio,
  SignpostBig,
  UsersRound,
  TrendingUp,
  LayoutGrid,
  DollarSign,
  Building2,
  ShieldAlert,
  Send,
  Ticket,
  ChartColumn,
} from 'lucide-react';
import SidebarToggleButton from './SidebarToggleButton';
import { useFooterMenu } from '@/app/hooks/useFooterMenu';
import {
  getAllSignalCount,
  getLinkedInSignalCount,
  getMarketSignalCount,
  getWebsiteSignalCount,
} from '@/api/signals';
import { useEntitlementGate } from '@/app/hooks/use-entitlement-gate';
import EntitlementGateModal from '@/app/components/entitlement/EntitlementGateModal';

export default function MainSidebar() {
  const pathname = usePathname();
  const { clientId } = useParams<{ clientId: string }>();
  const { isSidebarOpen, setIsSidebarOpen, isSidebarCollapsed, setIsSidebarCollapsed } =
    useSidebar();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const { client, clientList, setClientList, setClient, user } = useUser();
  const t = useTranslations('sidebarNav');
  const tOps = useTranslations('opsDashboard');
  const commonT = useTranslations('common');
  const isOpsDashboard = pathname.startsWith('/ops-dashboard');

  const opsNavItems = useMemo(() => {
    return [
      {
        href: '/ops-dashboard/workspace',
        lucideIcon: Building2,
        label: tOps('nav.workspace'),
        eventName: 'ops_nav_workspace',
      },
      {
        href: '/ops-dashboard/providers',
        lucideIcon: ChartColumn,
        label: tOps('nav.providers'),
        eventName: 'ops_nav_providers',
      },
      {
        href: '/ops-dashboard/website-signals',
        lucideIcon: Globe,
        label: tOps('nav.websiteSignals'),
        eventName: 'ops_nav_website_signals',
      },
      {
        href: '/ops-dashboard/market-signals',
        lucideIcon: TrendingUp,
        label: tOps('nav.marketSignals'),
        eventName: 'ops_nav_market_signals',
      },
      {
        href: '/ops-dashboard/linkedin-signals',
        lucideIcon: Linkedin,
        label: tOps('nav.linkedinSignals'),
        eventName: 'ops_nav_linkedin_signals',
      },
      {
        href: '/ops-dashboard/kb-readiness',
        lucideIcon: BookOpenCheck,
        label: tOps('nav.kbReadiness'),
        eventName: 'ops_nav_kb_readiness',
      },
      {
        href: '/ops-dashboard/destinations',
        lucideIcon: Send,
        label: tOps('nav.destinations'),
        eventName: 'ops_nav_destinations',
      },
      {
        href: '/ops-dashboard/billing',
        lucideIcon: DollarSign,
        label: tOps('nav.billing'),
        eventName: 'ops_nav_billing',
      },
      {
        href: '/ops-dashboard/whitelist',
        lucideIcon: ShieldAlert,
        label: tOps('nav.whitelist'),
        eventName: 'ops_nav_whitelist',
      },
      {
        href: '/ops-dashboard/partner-codes',
        lucideIcon: Ticket,
        label: tOps('nav.partnerCodes'),
        eventName: 'ops_nav_partner_codes',
      },
    ];
  }, [tOps]);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isSidebarPeekOpen, setIsSidebarPeekOpen] = useState(false);
  const [isSidebarCollapsing, setIsSidebarCollapsing] = useState(false);
  const [pendingClient, setPendingClient] = useState<Client | null>(null);
  const peekCloseTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const collapseTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    setCompanySignalCount,
    contactSignalCount,
    setContactSignalCount,
    marketSignalCount,
    setMarketSignalCount,
    linkedInSignalCount,
    setLinkedInSignalCount,
    totalSignalCount,
    setTotalSignalCount,
  } = useSignalCount();
  const { trackEvent } = useIntercom();
  const { guard, isCheckingGate, modalState, closeModal, openPlans } = useEntitlementGate(clientId);
  const footerMenu = useFooterMenu();

  const queryClient = useQueryClient();

  const { data: queriedClient, refetch: refetchClient } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => getClient(clientId || ''),
    enabled: !!clientId,
    initialData: client?.id === clientId ? client : undefined,
  });
  const effectiveClient = queriedClient ?? (client?.id === clientId ? client : null);
  const { toast } = useToast();
  const [enablingSignal, setEnablingSignal] = useState<string | null>(null);
  const enableSignal = async (signalType: 'website' | 'market' | 'linkedin') => {
    if (!clientId || enablingSignal) return;
    try {
      setEnablingSignal(signalType);
      await guard({ resource: 'client', action: 'update' }, async () => {
        await updateClient(clientId, { signal: { [signalType]: { enabled: true } } });
        const { data } = await refetchClient({ throwOnError: true });
        if (data) setClient(data);
      });
    } catch (error) {
      toast({ title: getErrorMessage(error), toastType: 'error' });
    } finally {
      setEnablingSignal(null);
    }
  };

  const trackNav = (eventName: string) => {
    trackEvent(eventName, { current_path: window.location.pathname, surface: 'nav_menu' });
  };

  const clearSidebarCounts = React.useCallback(() => {
    setTotalSignalCount(null);
    setCompanySignalCount(null);
    setContactSignalCount(null);
    setMarketSignalCount(null);
    setLinkedInSignalCount(null);
  }, [
    setTotalSignalCount,
    setCompanySignalCount,
    setContactSignalCount,
    setMarketSignalCount,
    setLinkedInSignalCount,
  ]);

  const previousClientIdRef = React.useRef(clientId);

  React.useEffect(() => {
    if (previousClientIdRef.current && previousClientIdRef.current !== clientId) {
      clearSidebarCounts();
    }
    previousClientIdRef.current = clientId;
  }, [clearSidebarCounts, clientId]);

  const menuBeforeSignals = useMemo(() => {
    return [
      // {
      //   href: user?.type === 'internal' ? '/matt/chat' : '#',
      //   icon: 'logo-nav',
      //   label: 'Chat',
      // },
      {
        href: `/${clientId}/home`,
        lucideIcon: House,
        label: t('home'),
        eventName: 'nav_menu_home',
      },
      // {
      //   href: `/${clientId}/tasks`,
      //   lucideIcon: SquareCheckBig,
      //   label: t('tasks'),
      //   eventName: 'nav_menu_tasks',
      // },
    ];
  }, [clientId, t]);

  const menuAfterSignals = useMemo(() => {
    return [
      {
        href: `/${clientId}/buyers`,
        lucideIcon: UsersRound,
        label: t('contacts'),
        eventName: 'nav_menu_buyers',
      },
      {
        href: `/${clientId}/target-list`,
        lucideIcon: ListTodo,
        label: t('targetList'),
        eventName: 'nav_menu_target_lists',
      },
      {
        href: `/${clientId}/destinations`,
        lucideIcon: SignpostBig,
        label: t('integrations'),
        eventName: 'nav_menu_destinations',
      },
    ];
  }, [clientId, t]);

  const router = useRouter();
  const [debouncedClientSearch, setDebouncedClientSearch, updateClientSearchImmediate] =
    useDebounce('', 500);
  const normalizedClientSearch = debouncedClientSearch.trim();
  const { data: searchedClientList, isFetching: isSearchingClients } = useQuery({
    queryKey: ['sidebar-clients', normalizedClientSearch],
    queryFn: () => getClients(undefined, undefined, normalizedClientSearch),
    enabled: isDropdownOpen && normalizedClientSearch.length > 0,
  });

  const { data: totalSignalCountData } = useQuery({
    queryKey: ['all-signal-count', clientId],
    queryFn: () => getAllSignalCount(clientId || ''),
    enabled: !!clientId,
  });
  const { data: websiteSignalCountData } = useQuery({
    queryKey: ['website-signal-count', clientId],
    queryFn: () => getWebsiteSignalCount(clientId || ''),
    enabled: !!clientId,
  });
  const { data: marketSignalCountData } = useQuery({
    queryKey: ['market-signal-count', clientId],
    queryFn: () => getMarketSignalCount(clientId || ''),
    enabled: !!clientId,
  });
  const { data: linkedInSignalCountData } = useQuery({
    queryKey: ['linkedin-signal-count', clientId],
    queryFn: () => getLinkedInSignalCount(clientId || ''),
    enabled: !!clientId,
  });

  React.useEffect(() => {
    setTotalSignalCount(totalSignalCountData?.total ?? null);
  }, [setTotalSignalCount, totalSignalCountData]);

  React.useEffect(() => {
    setContactSignalCount(websiteSignalCountData?.total ?? null);
  }, [setContactSignalCount, websiteSignalCountData]);

  React.useEffect(() => {
    setMarketSignalCount(marketSignalCountData?.total ?? null);
  }, [marketSignalCountData, setMarketSignalCount]);

  React.useEffect(() => {
    setLinkedInSignalCount(linkedInSignalCountData?.total ?? null);
  }, [linkedInSignalCountData, setLinkedInSignalCount]);

  const dropdownClients = useMemo(() => {
    const list = normalizedClientSearch ? searchedClientList : clientList;
    const clients = [...(list?.data || [])];

    const currentClientForDropdown = effectiveClient ?? client;
    if (
      currentClientForDropdown &&
      !clients.some(item => item.id === currentClientForDropdown.id)
    ) {
      clients.unshift(currentClientForDropdown);
    }

    return clients;
  }, [client, clientList, effectiveClient, normalizedClientSearch, searchedClientList]);

  const activeClient = useMemo(() => {
    if (!clientId) return null;
    if (pendingClient && pendingClient.id === clientId) return pendingClient;
    if (effectiveClient && effectiveClient.id === clientId) return effectiveClient;
    return clientList?.data.find(item => item.id === clientId) ?? null;
  }, [effectiveClient, clientId, clientList, pendingClient]);

  React.useEffect(() => {
    if (clientId && effectiveClient && effectiveClient.id === clientId) {
      setPendingClient(null);
      if (client?.id !== effectiveClient.id) {
        setClient(effectiveClient);
      }
    }
  }, [effectiveClient, clientId, client?.id, setClient]);

  const clearClientSearch = () => {
    setClientSearch('');
    updateClientSearchImmediate('');
  };

  const closeSidebarDrawer = () => {
    setIsSidebarOpen(false);
    setIsSidebarPeekOpen(false);
  };

  const renderClientOption = (item: Client) => (
    <div className="flex items-center gap-2 overflow-hidden">
      {item.logo ? (
        <img src={item.logo} alt={item.name} className="w-5 h-5 rounded object-contain shrink-0" />
      ) : (
        <div className="w-5 h-5 bg-border rounded flex items-center justify-center shrink-0">
          <Icon name="building-4-line" size={3} className="text-muted-foreground" />
        </div>
      )}
      <span className="overflow-hidden text-ellipsis whitespace-nowrap">{item.name}</span>
    </div>
  );

  const onClientClick = (value: string) => {
    if (value === 'add_new_client') {
      setIsAddClientModalOpen(true);
      return;
    }
    clearClientSearch();
    setIsDropdownOpen(false);
    closeSidebarDrawer();
    if (value !== clientId) {
      const selectedClient = dropdownClients.find(item => item.id === value);
      if (selectedClient) {
        setPendingClient(selectedClient);
        setClient(selectedClient);
      }
      clearSidebarCounts();
      queryClient.cancelQueries({
        predicate: query => query.state.fetchStatus === 'fetching',
      });
    }
    router.push(`/${value}/home`);
  };

  const handleSidebarToggle = () => {
    trackNav('nav_menu_sidebar_toggle');
    setIsDropdownOpen(false);
    clearClientSearch();
    setIsSidebarPeekOpen(false);

    if (isSidebarCollapsed) {
      setIsSidebarCollapsed(false);
      return;
    }

    setIsSidebarCollapsing(true);
    setIsSidebarCollapsed(true);
    collapseTimeoutRef.current = setTimeout(() => {
      setIsSidebarCollapsing(false);
      collapseTimeoutRef.current = null;
    }, 300);
  };

  const cancelPeekClose = () => {
    if (peekCloseTimeoutRef.current) {
      clearTimeout(peekCloseTimeoutRef.current);
      peekCloseTimeoutRef.current = null;
    }
  };

  const schedulePeekClose = () => {
    cancelPeekClose();
    peekCloseTimeoutRef.current = setTimeout(() => {
      if (!isDropdownOpen) {
        setIsSidebarPeekOpen(false);
      }
    }, 120);
  };

  return (
    <>
      {isSidebarCollapsed && !isSidebarPeekOpen && (
        <div
          className="fixed inset-y-0 left-0 z-[48] hidden w-2 min-[980px]:block"
          onMouseEnter={() => {
            cancelPeekClose();
            setIsSidebarPeekOpen(true);
          }}
          aria-hidden="true"
        />
      )}

      {isSidebarPeekOpen && (
        <div
          className="fixed inset-y-0 left-0 z-[48] hidden w-[304px] min-[980px]:block"
          onMouseEnter={cancelPeekClose}
          onMouseLeave={schedulePeekClose}
          aria-hidden="true"
        />
      )}

      {(isSidebarOpen || isSidebarPeekOpen) && (
        <div
          className={cn(
            'fixed inset-0 bg-strong-950/20 z-40',
            isSidebarPeekOpen ? 'hidden min-[980px]:block' : 'min-[980px]:hidden'
          )}
          onClick={() => {
            closeSidebarDrawer();
          }}
        />
      )}

      <div
        className={cn(
          'w-0 shrink-0',
          'min-[980px]:relative min-[980px]:h-screen min-[980px]:transition-[width] min-[980px]:duration-300 min-[980px]:ease-in-out',
          !isSidebarCollapsed && 'min-[980px]:w-[280px]'
        )}
      >
        <aside
          className={cn(
            'flex flex-col bg-background border border-border overflow-hidden',
            'fixed top-3 bottom-3 left-3 z-[49] h-auto w-[280px] max-w-[calc(100vw-24px)] rounded-xl shadow-lg',
            'transition-transform duration-300 ease-in-out will-change-transform',
            '[transform:translate3d(var(--sidebar-mobile-translate),0,0)]',
            'min-[980px]:relative min-[980px]:top-0 min-[980px]:bottom-auto min-[980px]:left-0 min-[980px]:h-screen min-[980px]:max-w-none min-[980px]:rounded-none min-[980px]:border-y-0 min-[980px]:border-l-0 min-[980px]:shadow-none',
            'min-[980px]:[transform:translate3d(var(--sidebar-desktop-translate),0,0)]',
            isSidebarCollapsed &&
              !isSidebarCollapsing &&
              'min-[980px]:fixed min-[980px]:top-3 min-[980px]:bottom-3 min-[980px]:left-3 min-[980px]:h-auto min-[980px]:w-[280px] min-[980px]:rounded-xl min-[980px]:border min-[980px]:shadow-lg'
          )}
          style={
            {
              '--sidebar-mobile-translate': isSidebarOpen ? '0px' : 'calc(-100% - 12px)',
              '--sidebar-desktop-translate':
                !isSidebarCollapsed || isSidebarPeekOpen ? '0px' : 'calc(-100% - 12px)',
            } as React.CSSProperties
          }
          onMouseEnter={cancelPeekClose}
          onMouseLeave={() => {
            if (isSidebarPeekOpen) {
              schedulePeekClose();
            }
          }}
        >
          <div className="py-[14px] px-[13px] border-b border-border flex items-center h-[56px]">
            <div className="flex items-center gap-2 w-full min-w-0">
              <div className="flex items-center flex-1 min-w-0">
                {isOpsDashboard ? (
                  <div className="flex items-center gap-2 w-full overflow-hidden">
                    <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-xs font-semibold text-primary-foreground">
                      1e
                    </div>
                    <div className="flex flex-col overflow-hidden leading-tight">
                      <span className="text-[14px] font-semibold text-foreground truncate">
                        {tOps('title')}
                      </span>
                      <span className="text-[11px] text-muted-foreground truncate">
                        {tOps('internalOps')}
                      </span>
                    </div>
                  </div>
                ) : user?.type === 'external' ? (
                  <div className="flex items-center gap-2 w-full overflow-hidden">
                    <div
                      className={cn(
                        'relative w-6 h-6 flex items-center justify-center rounded-md shrink-0',
                        pathname === '/home' && 'bg-border'
                      )}
                    >
                      <ClientLogo className="w-6 h-6 rounded" />
                    </div>
                    <div className="flex flex-col overflow-hidden">
                      <span className="text-foreground text-[15px] font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                        {activeClient?.name || '-'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    {clientList || client ? (
                      clientList && 'data' in clientList ? (
                        <div className="relative flex items-center justify-between gap-2 w-full overflow-hidden">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <div
                              className={cn(
                                'relative w-6 h-6 flex items-center justify-center rounded-md shrink-0',
                                pathname === '/home' && 'bg-border'
                              )}
                            >
                              <ClientLogo
                                className="w-6 h-6 rounded"
                                client={activeClient || undefined}
                              />
                            </div>
                            <div className="flex flex-col overflow-hidden">
                              <span className="text-foreground text-[15px] font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                                {activeClient?.name || '-'}
                              </span>
                            </div>
                          </div>
                          <Dropdown
                            contentClassName="cursor-pointer w-[248px]"
                            scrollableClassName="max-h-[400px] overflow-y-auto"
                            menuItemClassName="pr-2"
                            dropdownMenuProps={{
                              open: isDropdownOpen,
                              onOpenChange: open => {
                                setIsDropdownOpen(open);
                                if (!open) {
                                  clearClientSearch();
                                }
                              },
                              modal: false,
                            }}
                            dropdownMenuContentProps={{
                              align: 'end',
                              side: 'bottom',
                              sideOffset: 10,
                            }}
                            header={
                              <div
                                className="border-b border-border p-1 pb-2"
                                onClick={e => e.stopPropagation()}
                              >
                                <Input
                                  value={clientSearch}
                                  placeholder={commonT('search')}
                                  prefix={
                                    <Icon
                                      name={isSearchingClients ? 'loader-2-fill' : 'search'}
                                      className={cn(
                                        'text-muted-foreground',
                                        isSearchingClients && 'animate-spin'
                                      )}
                                      size={4}
                                    />
                                  }
                                  suffix={
                                    clientSearch ? (
                                      <button
                                        type="button"
                                        className="h-4 w-4 flex items-center justify-center bg-muted rounded-full text-foreground"
                                        onClick={() => {
                                          setClientSearch('');
                                          updateClientSearchImmediate('');
                                        }}
                                      >
                                        <Icon name="cross-fill" size={3.5} />
                                      </button>
                                    ) : null
                                  }
                                  onChange={e => {
                                    setClientSearch(e.target.value);
                                    setDebouncedClientSearch(e.target.value);
                                  }}
                                  onKeyDown={e => e.stopPropagation()}
                                  wrapperClassName="h-8 min-w-0"
                                  className="h-8 text-sm"
                                  prefixWrapperClassName="py-1.5 px-2 pl-2.5 items-center h-8"
                                  suffixWrapperClassName="inline-flex items-center pr-2"
                                />
                              </div>
                            }
                            menuItems={
                              dropdownClients.map(item => ({
                                label: renderClientOption(item),
                                value: item.id,
                                isSelected: item.id === clientId,
                              })) || []
                            }
                            footer={
                              <div className="border-t border-border p-1">
                                {isSearchingClients && (
                                  <div className="flex relative h-30">
                                    <Spinner size={5} />
                                  </div>
                                )}
                                <div
                                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setIsDropdownOpen(false);
                                    onClientClick('add_new_client');
                                  }}
                                >
                                  <Button
                                    variant="outline"
                                    className="w-full h-8 gap-1 pointer-events-none"
                                  >
                                    <Icon name="add-fill" size={4} />
                                    {t('add')}
                                  </Button>
                                </div>
                              </div>
                            }
                            onItemClick={(value: string) => {
                              onClientClick(value);
                            }}
                          >
                            {/* Spec: 28×28 trigger — hover / open / active states */}
                            <div
                              className={cn(
                                'flex shrink-0 cursor-pointer items-center justify-center rounded-[6px] border transition-all duration-150',
                                'h-7 w-7',
                                // Default: light gray border, white bg, gray icon
                                'border-border bg-background text-muted-foreground',
                                // Hover (only when closed): light blue bg, border softens
                                !isDropdownOpen && 'hover:bg-primary/5 hover:border-primary/20',
                                // Focus: prominent gray border, no color shift
                                'focus-visible:outline-none focus-visible:border-muted-foreground/40',
                                // Active: press-down
                                'active:scale-[0.96]',
                                // Open: blue border + blue bg + blue icon, hover won't override
                                isDropdownOpen && 'border-primary bg-primary/10 text-primary'
                              )}
                            >
                              <ChevronDown
                                size={14}
                                className={cn(
                                  'transition-transform duration-200',
                                  isDropdownOpen && 'rotate-180'
                                )}
                              />
                            </div>
                          </Dropdown>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 w-full border border-border px-2 py-2 rounded-md">
                          <ClientLogo className="w-6 h-6 rounded" client={client || undefined} />
                          <span className="text-foreground text-[15px] font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                            {client?.name}
                          </span>
                        </div>
                      )
                    ) : (
                      <div className="flex items-center gap-2 w-full border border-weak-50 bg-secondary px-1 py-1 h-10 rounded-md"></div>
                    )}
                  </>
                )}
              </div>
              <SidebarToggleButton
                label={isSidebarCollapsed ? t('expandSidebar') : t('collapseSidebar')}
                onClick={handleSidebarToggle}
                className="hidden min-[980px]:inline-flex"
              />
            </div>
          </div>
          {/* main Menu */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <CustomScroll
              orientation="vertical"
              viewportClassName="pr-0 [&>div]:!h-full [&>div>div]:h-full"
            >
              <div className="flex h-full min-h-full flex-col">
                <nav className="flex w-full flex-col gap-2 p-2 pb-6">
                  {isOpsDashboard ? (
                    <div className="flex flex-col gap-0.5">
                      {opsNavItems.map(item => (
                        <Link
                          href={item.href}
                          key={item.href}
                          className="focus:outline-none"
                          onClick={() => {
                            closeSidebarDrawer();
                            trackNav(item.eventName);
                          }}
                        >
                          <SidebarMenuItem
                            active={pathname.includes(item.href)}
                            lucideIcon={item.lucideIcon}
                            className="whitespace-nowrap"
                            wrapperClassName="px-0"
                            label={item.label}
                          />
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col gap-0.5">
                        {menuBeforeSignals.map(item => (
                          <Link
                            href={item.href}
                            key={item.href}
                            className="focus:outline-none"
                            onClick={() => {
                              closeSidebarDrawer();
                              trackNav(item.eventName);
                            }}
                          >
                            <SidebarMenuItem
                              active={pathname.includes(item.href)}
                              lucideIcon={(item as any).lucideIcon}
                              icon={(item as any).icon as IconName}
                              className="whitespace-nowrap"
                              wrapperClassName="px-0"
                              label={item.label}
                            />
                          </Link>
                        ))}

                        <Link
                          href={`/${clientId}/signals`}
                          onClick={() => {
                            closeSidebarDrawer();
                            trackNav('nav_menu_signals');
                          }}
                          className={cn(
                            'sidebar-nav-item group flex cursor-pointer items-center justify-between rounded-[10px] px-2 py-1.5',
                            'transition-all duration-300',
                            'focus:outline-none',
                            pathname === `/${clientId}/signals` ||
                              pathname === `/${clientId}/signals/`
                              ? 'bg-primary/10 text-primary'
                              : 'bg-background text-secondary-foreground hover:bg-primary/5 hover:text-foreground active:scale-[0.98] active:translate-y-px'
                          )}
                          data-active={
                            pathname === `/${clientId}/signals` ||
                            pathname === `/${clientId}/signals/`
                              ? true
                              : undefined
                          }
                        >
                          {(() => {
                            const isActive =
                              pathname === `/${clientId}/signals` ||
                              pathname === `/${clientId}/signals/`;
                            return (
                              <>
                                <div className="flex flex-1 items-center">
                                  <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                                    <Radio
                                      size={16}
                                      className={cn(
                                        'transition-transform duration-200',
                                        isActive ? 'scale-[1.05]' : 'group-hover:scale-[1.05]'
                                      )}
                                    />
                                  </div>
                                  <span
                                    className={cn(
                                      'pl-2 text-[14px] transition-colors',
                                      isActive ? 'font-medium' : 'group-hover:font-medium'
                                    )}
                                  >
                                    {t('visitors')}
                                  </span>
                                </div>
                                {totalSignalCount !== null && (
                                  <Pill
                                    variant="soft"
                                    color="info"
                                    size="sm"
                                    className="min-w-[22px] justify-center"
                                  >
                                    {formatNumber(totalSignalCount)}
                                  </Pill>
                                )}
                              </>
                            );
                          })()}
                        </Link>

                        <div className="pl-[18px] py-0">
                          <div className="flex flex-col border-l border-l-border gap-1 pl-2.5 py-0.5">
                            {[
                              {
                                signalType: 'website' as const,
                                label: t('websiteSignals'),
                                href: `/${clientId}/signals/website`,
                                count: contactSignalCount,
                                eventName: 'nav_menu_signals_website',
                                icon: Globe,
                              },
                              {
                                signalType: 'market' as const,
                                label: t('marketSignals'),
                                href: `/${clientId}/signals/market`,
                                count: marketSignalCount,
                                eventName: 'nav_menu_signals_market',
                                icon: Megaphone,
                              },
                              {
                                signalType: 'linkedin' as const,
                                label: t('linkedinSignals'),
                                href: `/${clientId}/signals/linkedin`,
                                count: linkedInSignalCount,
                                eventName: 'nav_menu_signals_linkedin',
                                icon: Linkedin,
                              },
                            ].map(item => {
                              const isActive =
                                pathname === item.href || pathname === `${item.href}/`;
                              const ItemIcon = item.icon;
                              const signal = effectiveClient?.signal?.[item.signalType];
                              const disabled =
                                typeof signal === 'object' && signal !== null
                                  ? signal.enabled === false
                                  : signal === false;

                              return (
                                <div key={item.href} className="flex items-center gap-2">
                                  <Link
                                    href={item.href}
                                    onClick={() => {
                                      closeSidebarDrawer();
                                      trackNav(item.eventName);
                                    }}
                                    data-active={isActive || undefined}
                                    className={cn(
                                      'sidebar-nav-item group flex min-w-0 flex-1 cursor-pointer items-center gap-2 whitespace-nowrap rounded-[10px] px-2 py-1.5',
                                      'transition-all duration-300',
                                      'focus:outline-none',
                                      isActive
                                        ? 'bg-primary/10 text-primary'
                                        : 'bg-background text-secondary-foreground hover:bg-primary/5 hover:text-foreground active:scale-[0.98] active:translate-y-px'
                                    )}
                                  >
                                    <ItemIcon
                                      size={16}
                                      className={cn(
                                        'transition-transform duration-200',
                                        isActive ? 'scale-[1.05]' : 'group-hover:scale-[1.05]'
                                      )}
                                    />
                                    <span
                                      className={cn(
                                        'flex-1 text-[14px] leading-5',
                                        isActive ? 'font-medium' : 'group-hover:font-medium'
                                      )}
                                    >
                                      {item.label}
                                    </span>
                                    {!disabled &&
                                      enablingSignal !== item.signalType &&
                                      item.count !== null && (
                                        <Pill
                                          variant="soft"
                                          color="info"
                                          size="sm"
                                          className="min-w-[22px] justify-center"
                                        >
                                          {formatNumber(item.count)}
                                        </Pill>
                                      )}
                                  </Link>
                                  {enablingSignal === item.signalType ? (
                                    <span
                                      className="mr-2 flex w-[22px] shrink-0 items-center justify-center"
                                      role="status"
                                    >
                                      <Icon
                                        name="loader-2-fill"
                                        size={6}
                                        className="animate-spin text-muted-foreground"
                                      />
                                    </span>
                                  ) : disabled ? (
                                    <Switch
                                      className="mr-2"
                                      checked={false}
                                      aria-label={item.label}
                                      disabled={Boolean(enablingSignal) || isCheckingGate}
                                      onCheckedChange={checked => {
                                        if (checked) void enableSignal(item.signalType);
                                      }}
                                    />
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {menuAfterSignals.map(item => (
                          <Link
                            href={item.href}
                            key={item.href}
                            className="focus:outline-none"
                            onClick={() => {
                              closeSidebarDrawer();
                              trackNav(item.eventName);
                            }}
                          >
                            <SidebarMenuItem
                              active={pathname.includes(item.href)}
                              lucideIcon={(item as any).lucideIcon}
                              icon={(item as any).icon as IconName}
                              className="whitespace-nowrap"
                              wrapperClassName="px-0"
                              label={item.label}
                            />
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </nav>
                {!isOpsDashboard && (
                  <div className="mt-auto">
                    <nav className="flex flex-col border-t border-border p-2 py-6">
                      {footerMenu.map(item => {
                        const menuItem = (
                          <SidebarMenuItem
                            active={pathname.startsWith(item.activeMatch || item.href)}
                            lucideIcon={item.icon}
                            label={item.label}
                            wrapperClassName="px-0"
                            className={cn(
                              'whitespace-nowrap',
                              item.comingSoon &&
                                'cursor-default hover:bg-transparent hover:text-muted-foreground'
                            )}
                            action={
                              item.comingSoon ? (
                                <Pill variant="soft" color="neutral" size="sm">
                                  {t('comingSoon')}
                                </Pill>
                              ) : undefined
                            }
                          />
                        );

                        return item.comingSoon ? (
                          <div key={item.href}>{menuItem}</div>
                        ) : (
                          <Link
                            href={item.href}
                            key={item.href}
                            onClick={() => {
                              closeSidebarDrawer();
                              trackNav(item.eventName);
                            }}
                          >
                            {menuItem}
                          </Link>
                        );
                      })}
                    </nav>
                  </div>
                )}
              </div>
            </CustomScroll>
          </div>
          {!isOpsDashboard && clientId && (
            <div className="shrink-0 border-t border-border px-2 py-4">
              <SidebarPlanCard planHref={`/${clientId}/billing`} clientId={clientId} />
            </div>
          )}
          {isOpsDashboard ? (
            <div className="px-2 py-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => router.push('/dashboard')}
                className="w-full text-xs"
              >
                <LayoutGrid size={15} className="mr-1.5" />
                {tOps('goToDashboard')}
              </Button>
            </div>
          ) : (
            (user?.type === 'internal' || user?.type === 'agency') && (
              <div className="px-2 py-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => router.push('/dashboard')}
                  className="w-full"
                >
                  <ArrowLeft
                    size={16}
                    className="transition-transform duration-200 group-hover/btn:-translate-x-0.5"
                  />
                  {t('backtoDashboard')}
                </Button>
              </div>
            )
          )}
        </aside>
      </div>

      <AddClientModal
        isOpen={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
        onSuccess={async () => {
          try {
            const newClientList = await getClients();
            setClientList(newClientList);
          } catch (e) {
            console.error('Failed to refresh client list', e);
          }
        }}
      />
      <EntitlementGateModal
        open={modalState.isOpen}
        variant={modalState.variant}
        gateResult={modalState.gateResult}
        onOpenChange={open => {
          if (!open) closeModal();
        }}
        onViewPlans={openPlans}
      />
    </>
  );
}
